import React, { useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import FloatingAssistant from "@/components/assistant/FloatingAssistant";
import {
  LogOut, Menu, X, ChevronDown,
} from "lucide-react";

import Brand from "@/components/lifted/Brand";
import DashboardSidebar from "@/components/dashboard/DashboardSidebar";
import NavigationAssistant from "@/components/help/NavigationAssistant";
import AdminOrderAlertsBadge from "@/components/admin/AdminOrderAlertsBadge";
import useRealtime from "@/lib/useRealtime";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/permissions";
import { SHELL } from "@/constants/testIds";

function formatBadge(n) {
  if (!n) return null;
  if (n > 99) return "99+";
  return String(n);
}

export default function AppShell() {
  const { user, activeRole, setActiveRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);

  // Phase 17 — realtime unread count drives the hamburger + sidebar badge
  const { unread } = useRealtime(user);
  const badge = formatBadge(unread);

  if (!user) return null;

  const onLogout = async () => {
    await logout();
    // Phase 19 — force a hard navigation to defeat any cached React /
    // SSE / axios state so the next paint guarantees a fresh /auth/me
    // call (which will fail-fast since cookies are gone) and never
    // shows a previous user's footer.
    window.location.replace("/auth/login");
  };

  return (
    <div className="min-h-screen flex">
      {/* Phase 13.9 — Role-aware Lifted Supplies admin sidebar */}
      <DashboardSidebar />

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-30 backdrop-blur-md bg-[#050608]/70 border-b border-white/5">
          <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-10 py-3">
            <button
              className="lg:hidden text-lifted-text relative"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-hamburger"
            >
              <Menu className="h-6 w-6" />
              {badge && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="mobile-hamburger-unread"
                >
                  {badge}
                </span>
              )}
            </button>
            <div className="lg:hidden"><Brand compact /></div>

            <div className="hidden lg:block" data-testid="appshell-topbar-text">
              <div className="font-bebas text-[0.7rem] tracking-[0.32em] text-lifted-green drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                {ROLE_LABELS[activeRole] || ROLE_LABELS[user.primary_role]}
              </div>
              <div className="font-anton text-xl uppercase text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)]">
                {greetingFor(location.pathname)}
              </div>
            </div>

            <div className="ml-auto flex items-center gap-3">
              {/* Iter82 — Admin-only real-time order alert badge */}
              <AdminOrderAlertsBadge />
              {/* Phase 3A.6 — prominent "Back to Lifted Home" button. Visible
                  on every dashboard, both desktop topbar and mobile (next
                  to the View-As switcher). Routes to the public homepage. */}
              <Link
                to="/"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-lifted-green/15 border border-lifted-green/40 text-lifted-green font-bebas text-[0.7rem] tracking-[0.18em] uppercase hover:bg-lifted-green/25"
                data-testid="dashboard-back-home"
              >
                ← Back to Lifted Home
              </Link>
              <Link
                to="/"
                className="sm:hidden inline-flex items-center justify-center h-9 w-9 rounded-md bg-lifted-green/15 border border-lifted-green/40 text-lifted-green"
                aria-label="Back to Lifted Home"
                data-testid="dashboard-back-home-mobile"
              >
                ←
              </Link>
              {(() => {
                // Phase 15: Master Admin sees a "View As" preview switcher
                // covering all five business roles (Customer, Affiliate,
                // Driver, Partner, Franchisee) plus their own Master Admin
                // identity. Multi-role non-admin users still get their union.
                const isMaster = (user.roles || []).includes("master_admin");
                const viewAsOptions = isMaster
                  ? ["master_admin", "customer", "affiliate", "driver", "partner", "franchisee"]
                  : user.roles;
                const showSwitcher = isMaster || (user.roles && user.roles.length > 1);
                return showSwitcher && (
                <div className="relative" data-testid={SHELL.roleSwitcher}>
                  <button
                    onClick={() => setRoleDropdownOpen((v) => !v)}
                    className="btn-ghost !py-2 !px-3 text-[0.7rem]"
                  >
                    View as: <span className="text-lifted-green ml-1">{ROLE_LABELS[activeRole]}</span>
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </button>
                  {roleDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 glass-panel p-2 z-40">
                      {viewAsOptions.map((r) => (
                        <button
                          key={r}
                          data-testid={SHELL.roleSwitcherOption(r)}
                          onClick={() => { setActiveRole(r); setRoleDropdownOpen(false); }}
                          className={`w-full text-left px-3 py-2 rounded-md font-bebas tracking-[0.18em] text-sm ${
                            activeRole === r ? "bg-lifted-green/10 text-lifted-green" : "text-lifted-text hover:bg-white/5"
                          }`}
                        >
                          {ROLE_LABELS[r]}
                        </button>
                      ))}
                      {isMaster && (
                        <div className="mt-2 pt-2 border-t border-white/5 px-3 py-1 text-[10px] text-lifted-dim leading-relaxed">
                          View-as is a preview filter. You remain signed in as Master Admin.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })()}

              <div className="hidden sm:flex items-center gap-3 glass-panel px-3 py-1.5" data-testid={SHELL.userMenu}>
                <div className="h-7 w-7 rounded-full bg-lifted-green/15 border border-lifted-green/40 flex items-center justify-center font-anton text-lifted-green">
                  {(user.public_display_name || user.display_name || user.name || user.email)[0]?.toUpperCase()}
                </div>
                <div className="leading-tight">
                  <div className="text-xs text-lifted-dim">{user.email}</div>
                  <div data-testid={SHELL.userMenuName} className="font-bebas tracking-[0.18em] text-sm text-lifted-text">
                    {user.public_display_name || user.display_name || user.name}
                  </div>
                </div>
              </div>

              <button
                onClick={onLogout}
                className="hidden sm:inline-flex btn-ghost !py-2 !px-3 text-[0.7rem]"
                data-testid={SHELL.logout}
              >
                <LogOut className="h-3 w-3 mr-1" /> Sign out
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 lg:py-12">
          <Outlet />
        </main>
      </div>

      {/* Floating Lifted AI Assistant — every authenticated page */}
      <FloatingAssistant role={activeRole} />

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          {/* Phase 21.3 — drawer is `overflow-hidden flex-col`. The header
              and footer are `shrink-0`. Only the middle <nav> region scrolls.
              This guarantees the BRIAN HARRIS / Sign Out block always stays
              pinned at the BOTTOM of the drawer on every mobile viewport
              (iPhone SE → iPhone Pro Max → iPad Mini) and never floats
              between menu items. */}
          <div className="absolute left-0 top-0 bottom-0 w-[82%] max-w-[340px] bg-lifted-deep border-r border-white/10 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/5 shrink-0">
              <Brand />
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-drawer-close"
                className="text-lifted-text"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto" data-testid="mobile-drawer-scroll">
              {/* Phase 16.6 — full role-aware menu inside the drawer.
                  Tapping any link closes the drawer via onItemClick.
                  Phase 17 — `hideFooter` strips the redundant Invite/Footer
                  blocks so the AppShell sticky footer (profile + sign-out)
                  doesn't overlap menu items.
                  Phase 21.3 — `embedded` so DashboardSidebar drops its OWN
                  overflow-y-auto. The parent scroll region above is the
                  single source of truth for menu scrolling. */}
              <DashboardSidebar
                mobile
                embedded
                hideFooter
                unreadCount={unread}
                onItemClick={() => setMobileOpen(false)}
              />
            </div>
            <div
              className="border-t border-white/10 shrink-0 bg-black/95 backdrop-blur-md px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-2"
              data-testid="mobile-drawer-footer"
            >
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-full bg-lifted-green/20 border border-lifted-green/40 flex items-center justify-center font-anton text-lifted-green text-base">
                  {(user.name || user.email)[0]?.toUpperCase()}
                </div>
                <div className="leading-tight min-w-0 flex-1">
                  <div className="font-bebas tracking-[0.16em] text-base text-white truncate" data-testid="mobile-user-name">
                    {user.name || user.email}
                  </div>
                  <div className="text-[11px] text-lifted-dim truncate" data-testid="mobile-user-role">
                    {ROLE_LABELS[user.primary_role] || ROLE_LABELS[(user.roles || [])[0]] || "Member"}
                    {user.email ? ` · ${user.email}` : ""}
                  </div>
                </div>
              </div>
              <button
                onClick={onLogout}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-rose-500/15 border border-rose-500/40 text-rose-200 hover:bg-rose-500/25 text-sm font-bebas tracking-[0.18em] uppercase"
                data-testid="mobile-sign-out"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 16 — Floating navigation assistant (non-admin only, opt-out) */}
      <NavigationAssistant />
    </div>
  );
}

function greetingFor(path) {
  if (path.startsWith("/admin")) return "Admin command center";
  if (path.startsWith("/shop")) return "Lifted Shop";
  if (path.startsWith("/training")) return "Lifted Academy";
  if (path.startsWith("/affiliate")) return "Affiliate command";
  if (path.startsWith("/delivery")) return "Delivery operations";
  if (path.startsWith("/partner")) return "Partner network";
  if (path.startsWith("/franchise")) return "Franchise territories";
  if (path.startsWith("/reports")) return "Reporting layer";
  if (path.startsWith("/landing-pad")) return "Lifted Landing Pad";
  if (path.startsWith("/account") || path.startsWith("/settings")) return "Your account";
  return "Welcome to the ecosystem";
}

